import type { ThemeMode } from "../api/configuration";
import { THEME_PALETTE_VALUES } from "./themePaletteValues";

/**
 * Темата = ЦВЯТ + светла/тъмна. „Оранжево“ е базата (светлите стойности в
 * @theme на index.css, тъмната — блокът .dark); останалите цветове са по файл в
 * styles/palettes/ (светъл клас palette-<цвят>, тъмен — dark + palette-<цвят>-dark).
 * Подредбата е тази в модала „Тема“: оранжевото, после цветовете от демото
 * „Цветови теми“ (Claude Design).
 */
export const THEME_COLORS = [
  "orange",
  "green",
  "sky",
  "cream",
  "violet",
  "silver",
  "red",
] as const;

export type ThemeColor = (typeof THEME_COLORS)[number];

/** Цветовете на една тема в JS — САМО за двете места, които не могат да четат
 *  CSS променливите на текущата тема: мострите в модала „Тема“ (всяка карта
 *  показва ЧУЖДА тема) и antd ConfigProvider token-ите (искат реален hex).
 *  Стойностите са генерирани (themePaletteValues.ts). */
export interface ThemePaletteColors {
  canvas: string;
  surface: string;
  /** --color-pos-line — рамките на картите. */
  border: string;
  /** --color-pos-closed-bg — угасналата (закрита) маса в мострата. */
  inactive: string;
  ink: string;
  muted: string;
  /** --color-brand: плътен фон с бял текст; antd colorPrimary. */
  accent: string;
  /** --color-brand-deep: hover на плътния фон; antd colorPrimaryHover. */
  accentHover: string;
  /** Текст в цвета на акцента; antd colorLink. */
  accentText: string;
  /** --color-brand-tint / --color-brand-tint2; antd controlItemBgActive. */
  tint: string;
  tintBorder: string;
  /** --color-pos-accent — акцентът на „Маркиране“. */
  posAccent: string;
  /** Само тъмните: ниво 1 (модали, dropdown-и) за antd colorBgElevated,
   *  colorBorder и colorBorderSecondary. */
  elevated?: { surface: string; border: string; borderSecondary: string };
}

export interface ThemePalette extends ThemePaletteColors {
  mode: ThemeMode;
  color: ThemeColor;
  isDark: boolean;
  /** Класът на цвета върху <html> (тъмните носят и `dark`); оранжевият е
   *  базата и няма свой клас. */
  paletteClass: string | null;
}

/** Стойността в базата за цвят + светла/тъмна ("light"/"dark" са оранжевият). */
export function themeModeOf(color: ThemeColor, isDark: boolean): ThemeMode {
  if (color === "orange") return isDark ? "dark" : "light";
  return (isDark ? `${color}-dark` : color) as ThemeMode;
}

export const THEME_PALETTES: readonly ThemePalette[] = THEME_COLORS.flatMap(
  (color) =>
    [false, true].map((isDark) => {
      const mode = themeModeOf(color, isDark);
      return {
        ...THEME_PALETTE_VALUES[mode],
        mode,
        color,
        isDark,
        paletteClass:
          color === "orange"
            ? null
            : isDark
              ? `palette-${color}-dark`
              : `palette-${color}`,
      };
    }),
);

/** Палитрата на тема; непозната стойност (по-нов FE в базата) → оранжевата. */
export function themePaletteOf(mode: ThemeMode): ThemePalette {
  return (
    THEME_PALETTES.find((palette) => palette.mode === mode) ??
    THEME_PALETTES[0]
  );
}

/** i18n ключът на името на цвета (без „светла“/„тъмна“). */
export function themeColorLabelKey(color: ThemeColor): string {
  return `common.themes.${color}`;
}
